/**
 * @file order-attention-rows.ts
 * @description Строки и фильтры страницы «Требуют внимания» (phase19.13 шаг 5;
 * phase19.15; phase19.31 — таблица вместо карточек).
 *
 * Сервер отдаёт два разных списка: отгрузки, где доставка встала, и заказы,
 * где заявка на доставку ждёт расчёта. Таблице нужна одна строка одного
 * вида — здесь оба списка приводятся к `AttentionRow`: проблема, сколько
 * стоит, с какого события, перевозчик словом (не «SDEK»/«PICKUP»), трек.
 * Порядок строк серверный (по критичности и давности) — не пересортировываем,
 * иначе список разойдётся со счётчиком в меню.
 */

import {
  DELIVERY_STAGE_LABEL,
  ORDER_ATTENTION_RULE_SHORT,
  SHIPMENT_ATTENTION_RULE_TONE,
  formatElapsed,
  type OrderAttentionRule,
  type ShipmentAttentionListDto,
  type ShipmentAttentionRule,
} from "./api-delivery-monitoring";
import { SHIPMENT_CARRIER_LABEL, type ShipmentCarrier } from "./api-orders";
import { formatShopDateTime } from "./shop-time";

/** Фильтр-пилюля: все строки или одно правило. */
export type AttentionFilter = "all" | ShipmentAttentionRule | OrderAttentionRule;

/** Короткая подпись правила отгрузки — для пилюли и колонки «Проблема». */
export const SHIPMENT_RULE_SHORT: Record<ShipmentAttentionRule, string> = {
  not_delivered: "Не вручено",
  pickup_wait: "Лежит в ПВЗ",
  track_not_assigned: "Нет трека",
  no_movement: "Без движения",
};

/** Порядок пилюль = порядок серьёзности правил. */
const SHIPMENT_RULE_ORDER: ShipmentAttentionRule[] = ["not_delivered", "pickup_wait", "track_not_assigned", "no_movement"];
const ORDER_RULE_ORDER: OrderAttentionRule[] = ["delivery_agreement_sla"];

export interface AttentionRow {
  key: string;
  orderId: string;
  orderNumber: string;
  rule: ShipmentAttentionRule | OrderAttentionRule;
  /** «Без движения», «Согласование доставки». */
  problem: string;
  /** Сколько стоит: «4 дн», «6 ч». */
  elapsed: string;
  /** Красный — риск потерять посылку, янтарный — ждёт действия. */
  tone: "attention" | "error";
  /** С какого события ждём: «Принят перевозчиком». */
  event: string;
  /** Когда это событие было: «14.08.2026, 19:40». */
  since: string;
  /** Перевозчик словом; `null` — у строки заказа перевозчика нет. */
  carrier: string | null;
  track: string | null;
}

/** Цвет проблемы по тону строки — цветами статусов темы, не зашитыми. */
export const ATTENTION_TONE_CLASS: Record<AttentionRow["tone"], string> = {
  attention: "text-[var(--status-attention-fg,var(--warning))]",
  error: "text-[var(--status-error-fg,var(--destructive))]",
};

/** Код перевозчика сервера → название («SDEK» → «СДЭК»); неизвестный — как есть. */
export function carrierLabel(code: string): string {
  return SHIPMENT_CARRIER_LABEL[code as ShipmentCarrier] ?? code;
}

/** Ответ сервера → строки таблицы: сначала заявки по заказам, затем отгрузки. */
export function buildAttentionRows(data: ShipmentAttentionListDto): AttentionRow[] {
  const orders: AttentionRow[] = (data.orders ?? []).map((item) => ({
    key: `order-${item.orderId}`,
    orderId: item.orderId,
    orderNumber: item.orderNumber,
    rule: item.rule,
    problem: ORDER_ATTENTION_RULE_SHORT[item.rule],
    elapsed: formatElapsed(item.hoursElapsed),
    tone: "attention",
    event: "Заявка на доставку принята",
    since: formatShopDateTime(item.sinceAt),
    carrier: null,
    track: null,
  }));
  const shipments: AttentionRow[] = data.items.map((item) => ({
    key: `shipment-${item.shipmentId}`,
    orderId: item.orderId,
    orderNumber: item.orderNumber,
    rule: item.rule,
    problem: SHIPMENT_RULE_SHORT[item.rule],
    elapsed: formatElapsed(item.hoursElapsed),
    tone: SHIPMENT_ATTENTION_RULE_TONE[item.rule] === "destructive" ? "error" : "attention",
    event: item.stage ? DELIVERY_STAGE_LABEL[item.stage] : "Событий от перевозчика нет",
    since: formatShopDateTime(item.sinceAt),
    carrier: carrierLabel(item.carrier),
    track: item.trackNumber,
  }));
  return [...orders, ...shipments];
}

/** Пилюля фильтра: подпись, число, «опасный» ли тон. */
export interface AttentionChip {
  value: AttentionFilter;
  label: string;
  count: number;
  danger: boolean;
}

/** Пилюли со счётчиками: «Все» и по правилу (правила без строк не прячем — видно, что их ноль). */
export function attentionChips(data: ShipmentAttentionListDto): AttentionChip[] {
  const shipmentChips = SHIPMENT_RULE_ORDER.map((rule) => ({
    value: rule,
    label: SHIPMENT_RULE_SHORT[rule],
    count: data.counts.byRule[rule] ?? 0,
    danger: SHIPMENT_ATTENTION_RULE_TONE[rule] === "destructive",
  }));
  const orderChips = ORDER_RULE_ORDER.map((rule) => ({
    value: rule,
    label: ORDER_ATTENTION_RULE_SHORT[rule],
    count: data.counts.ordersByRule?.[rule] ?? (data.orders ?? []).filter((o) => o.rule === rule).length,
    danger: false,
  }));
  const total = data.items.length + (data.orders ?? []).length;
  return [{ value: "all", label: "Все", count: total, danger: false }, ...shipmentChips, ...orderChips];
}

export function filterAttentionRows(rows: AttentionRow[], filter: AttentionFilter): AttentionRow[] {
  return filter === "all" ? rows : rows.filter((row) => row.rule === filter);
}

/** Значение фильтра из адреса; неизвестное — «Все». */
export function parseAttentionFilter(raw: string | null): AttentionFilter {
  const known: string[] = ["all", ...SHIPMENT_RULE_ORDER, ...ORDER_RULE_ORDER];
  return raw && known.includes(raw) ? (raw as AttentionFilter) : "all";
}
